import React, { useState, useEffect, useRef } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import formatNumber from "../../../helpers/NumberFormat";
import ModalV2 from "../../shared/Modal/ModalV2";
import { useTransferMessageMutation } from "services/clientApi";
import moment from "moment";
import "./finish.scss";

const useNavigationBlocker = (enabled, attentionData, sendTransfer, hasSubmitted, navigate) => {
  useEffect(() => {
    if (!enabled) return;

    const handleBeforeUnload = (e) => {
      if (!hasSubmitted.current) {
        e.preventDefault();
        e.returnValue = "";
        sendTransfer({
          transaction_id: attentionData.id,
          client_response: false,
        });
        hasSubmitted.current = true;
      }
    };

    const handlePopState = (e) => {
      if (hasSubmitted.current) return;

      const confirmLeave = window.confirm(
        "You haven't confirmed the transaction. Are you sure you want to leave?"
      );
      if (confirmLeave) {
        sendTransfer({
          transaction_id: attentionData.id,
          client_response: false,
        }).then(() => {
          hasSubmitted.current = true;
          navigate("/");;
        });
      } else {
        window.history.pushState(null, document.title, window.location.href);
      }
    };

    window.addEventListener("beforeunload", handleBeforeUnload);
    window.history.pushState(null, document.title, window.location.href);
    window.addEventListener("popstate", handlePopState);

    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
      window.removeEventListener("popstate", handlePopState);
    };
  }, [enabled, attentionData.id, sendTransfer, hasSubmitted, navigate]);
};

const Finish = ({ message, attentionData }) => {
  const screenWidth = useSelector((state) => state?.common?.screenWidth);
  const [isMobile, setIsMobile] = useState("");
  const [sendTransfer] = useTransferMessageMutation();
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const hasSubmitted = useRef(false);

  useEffect(() => {
    if (screenWidth === "sm" || screenWidth === "xs") {
      setIsMobile(true);
    } else {
      setIsMobile(false);
    }
  }, [screenWidth]);

  useNavigationBlocker(true, attentionData, sendTransfer, hasSubmitted, navigate);

  const submitMessage = async (data) => {
    setLoading(true);
    hasSubmitted.current = true;
    const result = await sendTransfer({
      transaction_id: attentionData.id,
      client_response: data,
    });

    if ("error" in result) {
      navigate("/");
      console.log("error");
    } else {
      navigate("/");
      setLoading(false);
    }
  };

  useEffect(() => {
    const handleVisibilityChange = async () => {
      if (document.visibilityState === "hidden" && !hasSubmitted.current) {
        await sendTransfer({
          transaction_id: attentionData.id,
          client_response: false,
        });
        hasSubmitted.current = true;
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);
    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [attentionData.id, sendTransfer]);

  return (
    <div className="finish container-fluid d-flex justify-content-center align-items-center h-100">
      <div className=" modal-content card shadow-lg rounded-4 p-4 w-100 borderCustom" style={{ maxWidth: '850px' }}>
        <div className="text-center mb-4">
          <span className="unicol" style={{ fontSize: '64px' }}>&#9888;</span>
          <h3 className="unicol fw-bold my-2">Transaction </h3>
          <p className="text-muted">
            This marks the official closure of all data that has been entered, reviewed, and validated.
            <br />
            All necessary steps have been completed, and no further modifications or additions are expected.
          </p>
        </div>

        <div className="card mb-4 border-0 bg-light shadow-sm rounded-3 p-3">
          <h5 className="fw-bold text-dark border-bottom pb-2 text-muted">
            Transaction Amount and Commission
          </h5>
          <div className="d-flex justify-content-between flex-wrap mt-3">
            <div className="d-flex gap-2 align-items-center">
              <i className="fas fa-euro-sign text-secondary"></i>
              <span className="text-muted">Amount:</span>
              <span className="fw-bold text-dark">{formatNumber(attentionData.amount)} €</span>
            </div>
            <div className="d-flex gap-2 align-items-center">
              <i className="fas fa-handshake text-secondary"></i>
              <span className="text-muted text-secondary">Commission:</span>
              <span className="fw-bold text-dark">{formatNumber(attentionData.outgoing_fee)} €</span>
            </div>
          </div>
        </div>

        <div className="card mb-4 border-0 bg-light shadow-sm rounded-3 p-3">
          <h5 className="fw-bold text-dark border-bottom pb-2 text-muted">
            Recipient Bank Information
          </h5>
          <div className="d-flex justify-content-between flex-wrap mt-3">
            <div className="d-flex gap-2 align-items-center">
              <i className="fas fa-university text-secondary"></i>
              <span className="text-muted">Bank:</span>
              <span className="fw-bold text-dark">{attentionData.bank_name}</span>
            </div>
            <div className="d-flex gap-2 align-items-center">
              <i className="fas fa-random text-secondary"></i>
              <span className="text-muted">BIC/SWIFT:</span>
              <span className="fw-bold text-dark">{attentionData.swift_code}</span>
            </div>
          </div>

          <div className="d-flex justify-content-between flex-wrap mt-3">
            <div className="d-flex gap-2 align-items-center">
              <i className="fas fa-credit-card text-secondary"></i>
              <span className="text-muted">IBAN:</span>
              <span className="fw-bold text-dark">{attentionData.iban}</span>
            </div>
          </div>
        </div>

        <div className="card mb-4 border-0 bg-light shadow-sm rounded-3 p-3">
          <h5 className="fw-bold text-dark border-bottom pb-2 text-muted">Transaction Description</h5>
          <p className="fw-bold text-dark mt-2"><i className="fa-solid fa-align-left me-2" />{attentionData.description}</p>
        </div>

        <div className="card mb-4 border-0 bg-light shadow-sm rounded-3 p-3">
          <h5 className="fw-bold text-dark border-bottom pb-2 text-muted">Beneficiary Details</h5>
          <div className="d-flex justify-content-between flex-wrap mt-3">
            <div className="d-flex gap-2 align-items-center">
              <i className="fas fa-user text-secondary"></i>
              <span className="text-muted">Name:</span>
              <span className="fw-bold text-dark">{attentionData.beneficary_name}</span>
            </div>
            <div className="d-flex gap-2 align-items-center">
              <i className="fas fa-flag text-secondary"></i>
              <span className="text-muted">Country:</span>
              <span className="fw-bold text-dark">{attentionData.beneficary_country}</span>
            </div>
          </div>

          {/* <div className="d-flex justify-content-between flex-wrap mt-3">
            <div className="d-flex gap-2 align-items-center">
              <i className="fas fa-map-marker-alt text-secondary"></i>
              <span className="text-muted">Address:</span>
              <span className="fw-bold text-dark">{attentionData.beneficary_address}</span>
            </div>
          </div> */}
        </div>

        <div className="card mb-4 border-0 bg-light shadow-sm rounded-3 p-3">
          <h5 className="fw-bold text-dark border-bottom pb-2 text-muted">Transaction Date</h5>
          <div className="d-flex align-items-center gap-2 mt-2">
            <i className="fas fa-calendar-alt text-secondary"></i>
            <span className="fw-bold text-dark">
              {moment(attentionData.execution_date).format("DD/MM/YYYY")}
            </span>
          </div>
        </div>

        <div className="text-center mt-4">
          <h4 className="text-dark fw-bold text-muted">Do you want to continue?</h4>
          <div className="d-flex justify-content-center gap-5 mt-3 flex-wrap">
            {loading ? (
              <div style={{ padding: "50px" }}>
                <div
                  style={{ width: "50px", height: "50px" }}
                  className="spinner-border text-danger"
                  role="status"
                >
                  <span className="sr-only">Loading...</span>
                </div>
              </div>
            ) : (
              <>
                <h4
                  onClick={() => submitMessage(true)}
                  type="button"
                  className="btn w-25 custom-danger text-white backg-unicol"
                >
                  {loading ? "Loading..." : "Yes"}
                </h4>
                <h4
                  onClick={() => submitMessage(false)}
                  type="button"
                  className="btn w-25 custom-danger text-white backg-unicol"
                >
                  No
                </h4>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Finish;
